'use client';

import * as React from 'react';
import { type HTMLMotionProps, motion, type Transition } from 'motion/react';
import { type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/shared/utils/utils';
import { buttonVariants } from '@/shared/components/ui/button';

interface Ripple {
  id: number;
  x: number;
  y: number;
}

interface RippleButtonProps extends HTMLMotionProps<'button'>, VariantProps<typeof buttonVariants> {
  children: React.ReactNode;
  rippleClassName?: string;
  scale?: number;
  transition?: Transition;
  loading?: boolean;
}

const RippleButton = React.forwardRef<HTMLButtonElement, RippleButtonProps>(
  (
    {
      children,
      onClick,
      className,
      rippleClassName,
      scale = 10,
      transition = { duration: 0.6, ease: 'easeOut' },
      variant,
      size,
      loading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const [ripples, setRipples] = React.useState<Ripple[]>([]);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    React.useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement);

    const createRipple = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      const button = buttonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const newRipple: Ripple = {
        id: Date.now(),
        x,
        y,
      };

      setRipples(prev => [...prev, newRipple]);

      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== newRipple.id));
      }, 600);
    }, []);

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (loading) return;
        createRipple(event);
        if (onClick) {
          onClick(event);
        }
      },
      [createRipple, onClick, loading]
    );

    return (
      <motion.button
        ref={buttonRef}
        onClick={handleClick}
        whileTap={!loading && !disabled ? { scale: 0.95 } : undefined}
        whileHover={!loading && !disabled ? { scale: 1.05 } : undefined}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading...
          </>
        ) : (
          children
        )}
        {!loading &&
          ripples.map(ripple => (
            <motion.span
              key={ripple.id}
              initial={{ scale: 0, opacity: 0.5 }}
              animate={{ scale, opacity: 0 }}
              transition={transition}
              className={cn(
                'absolute bg-primary-foreground rounded-full size-5 pointer-events-none',
                rippleClassName
              )}
              style={{
                top: ripple.y - 10,
                left: ripple.x - 10,
              }}
            />
          ))}
      </motion.button>
    );
  }
);

RippleButton.displayName = 'RippleButton';

export { RippleButton, type RippleButtonProps };
